// Queries and mutations for the engagement detail page.
//
// All keys are prefixed with ["engagement", id] so a single
// invalidateQueries({ queryKey: ["engagement", id] }) refreshes everything
// related to one engagement.

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Engagement } from "@/types/engagement";
import type {
  Asset,
  ExecuteScanResponse,
  Finding,
  FindingStatus,
  FindingStats,
  NormalizedEvidence,
  ReportData,
  ScanConfig,
  ScanStatus,
  ScanTask,
  VerifyOwnershipResponse,
} from "@/types/engagement-detail";

// ─── Queries ──────────────────────────────────────────────────────────────────

export function useEngagement(id: string) {
  return useQuery({
    queryKey: ["engagement", id],
    queryFn: () => api<Engagement>(`/engagements/${id}`),
    refetchInterval: (query) =>
      query.state.data?.status === "scanning" ? 5_000 : false,
  });
}

export function useEngagementAssets(id: string) {
  return useQuery({
    queryKey: ["engagement", id, "assets"],
    queryFn: () => api<Asset[]>(`/engagements/${id}/assets/`),
  });
}

/** POST /engagements/:eid/assets/ — add a single asset. */
export function useAddAsset(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { asset_type: string; value: string; description?: string }) =>
      api<Asset>(`/engagements/${engagementId}/assets/`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "assets"] });
    },
  });
}

/** PATCH /engagements/:eid/assets/:aid — edit value (gated) or description. */
export function useUpdateAsset(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { assetId: string; value?: string; description?: string }) =>
      api<Asset>(`/engagements/${engagementId}/assets/${input.assetId}`, {
        method: "PATCH",
        body: { value: input.value, description: input.description },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "assets"] });
    },
  });
}

/** POST /engagements/:eid/change-domain — one-shot typo-fix that wipes
 *  scan history and resets the engagement back to DRAFT. Replaces the
 *  delete-and-recreate flow. Allowed exactly once per engagement. */
export interface ChangeDomainResponse {
  engagement: { id: string; status: string; domain_changed_at: string };
  asset: { id: string; value: string; ownership_verified: boolean };
}

export function useChangeEngagementDomain(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { assetId: string; newValue: string }) =>
      api<ChangeDomainResponse>(`/engagements/${engagementId}/change-domain`, {
        method: "POST",
        body: { asset_id: input.assetId, new_value: input.newValue },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId] });
      qc.invalidateQueries({ queryKey: ["engagements"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** POST /engagements/:eid/assets/:aid/verify-ownership */
export function useVerifyAssetOwnership(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      api<VerifyOwnershipResponse>(
        `/engagements/${engagementId}/assets/${assetId}/verify-ownership`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "assets"] });
    },
  });
}

export function useEngagementFindings(id: string) {
  return useQuery({
    queryKey: ["engagement", id, "findings"],
    queryFn: () => api<Finding[]>(`/engagements/${id}/findings/`),
  });
}

export function useEngagementFindingStats(id: string) {
  return useQuery({
    queryKey: ["engagement", id, "findings", "stats"],
    queryFn: () => api<FindingStats>(`/engagements/${id}/findings/stats`),
  });
}

export interface EvidenceFilters {
  q?: string;
  evidence_type?: string;
  sort_by?: string;
  order?: "asc" | "desc";
}

interface EvidencePage {
  evidences: NormalizedEvidence[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  next_offset: number | null;
}

const EVIDENCE_PAGE_SIZE = 100;

// Server-side search + sort + filter + offset pagination. The frontend
// renders pages flat and drives a "Load more" button via fetchNextPage.
export function useEngagementEvidence(id: string, filters: EvidenceFilters = {}) {
  return useInfiniteQuery({
    queryKey: ["engagement", id, "evidence", filters],
    queryFn: ({ pageParam = 0 }) => {
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.evidence_type) params.set("evidence_type", filters.evidence_type);
      if (filters.sort_by) {
        params.set("sort_by", filters.sort_by);
        params.set("order", filters.order ?? "desc");
      }
      params.set("limit", String(EVIDENCE_PAGE_SIZE));
      params.set("offset", String(pageParam));
      return api<EvidencePage>(`/engagements/${id}/evidence?${params}`);
    },
    initialPageParam: 0,
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });
}

/**
 * Polls /scan-status every 3 seconds while the engagement is scanning.
 * Pass enabled=false to pause polling (e.g. when not on the Scan tab).
 */
export function useEngagementScanStatus(id: string, enabled = true) {
  return useQuery({
    queryKey: ["engagement", id, "scan-status"],
    queryFn: () => api<ScanStatus>(`/engagements/${id}/scan-status`),
    refetchInterval: 3_000,
    enabled,
  });
}

export function useEngagementTasks(id: string, refetchMs: number | false = 5_000) {
  return useQuery({
    queryKey: ["engagement", id, "tasks"],
    queryFn: () => api<ScanTask[]>(`/engagements/${id}/tasks`),
    refetchInterval: refetchMs,
  });
}

export function useEngagementReport(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["engagement", id, "report"],
    queryFn: () => api<ReportData>(`/engagements/${id}/reports/data`),
    enabled,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useExecuteScan(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cfg: ScanConfig) => {
      const body: Record<string, unknown> = {
        tool_timeout: cfg.timeout,
        port_scan_mode: cfg.portMode,
      };
      if (cfg.deepValidation) body.deep_validation_authorized = true;
      return api<ExecuteScanResponse>(`/engagements/${engagementId}/execute`, {
        method: "POST",
        body,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId] });
      qc.invalidateQueries({ queryKey: ["engagements"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export interface UpdateFindingInput {
  findingId: string;
  status: FindingStatus | string;
  severity_final: string;
  reviewer_notes: string;
  proof_of_concept: string;
}

export function useUpdateFinding(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateFindingInput) => {
      return api(`/engagements/${engagementId}/findings/${input.findingId}`, {
        method: "PATCH",
        body: {
          status: input.status,
          severity_final: input.severity_final,
          reviewer_notes: input.reviewer_notes,
          proof_of_concept: input.proof_of_concept,
          reviewed_by: "pentester",
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "findings"] });
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "findings", "stats"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export interface RetestFindingInput {
  findingId: string;
  status: "retest_pass" | "retest_fail";
  notes?: string;
}

export function useRetestFinding(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RetestFindingInput) => {
      const params = new URLSearchParams({
        retest_status: input.status,
        retest_by: "pentester",
      });
      if (input.notes) params.set("retest_notes", input.notes);
      return api(
        `/engagements/${engagementId}/findings/${input.findingId}/retest?${params.toString()}`,
        { method: "POST" },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "findings"] });
    },
  });
}

export function useCancelTask(engagementId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => {
      return api(`/engagements/${engagementId}/tasks/${taskId}/cancel`, {
        method: "POST",
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engagement", engagementId, "tasks"] });
      qc.invalidateQueries({ queryKey: ["engagement", engagementId] });
    },
  });
}
